import { t } from "i18next";
import { Loader } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import RecorderForm, {
  type RecorderFormHandle,
} from "@/components/ui/audio-recorder/recorder-form";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSyncRecordingTranscription } from "@/hooks/linkedRecordingSession/useSyncRecordingTranscription";
import { toast } from "@/hooks/use-toast";
import { useVisitSubmit } from "@/hooks/visits/useVisitSubmit";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import type { LinkedRecordingSession } from "@/services/linkedRecordingSessions";
import { useAuthStore } from "@/store/auth.store";
import { TranscriptionStreamingList } from "../../audio-recorder/TranscriptionStreamingList";
import { VisitSubmissionNotice } from "../VisitSubmissionNotice";

interface SyncRecordingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  linkedRecordingSession: LinkedRecordingSession;
  title?: string;
}

export const SyncRecordingSheet = (
  props: SyncRecordingSheetProps,
): JSX.Element => {
  const { onOpenChange } = props;

  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { isProviderReady, finished, transcription, connectionError } =
    useSyncRecordingTranscription({
      roomId: props.linkedRecordingSession.id,
    });
  const {
    submitVisitWithoutFile,
    error,
    loading,
    result,
    recovery,
    canRetry,
    abandon,
  } = useVisitSubmit({ draftId: `linked:${props.linkedRecordingSession.id}` });
  const userData = useAuthStore((state) => state.userData);

  useEffect(() => {
    if (!loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("visits.sidebar.messages.uploadSuccess.title"),
          description: t("visits.sidebar.messages.uploadSuccess.description"),
        });
        onOpenChange(false);
      }
      if (error) {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: error,
        });
      }
    }
  }, [result, error, loading, onOpenChange]);

  const handleFormSubmit = async (
    formData: RecorderFormSchemaType,
  ): Promise<void> => {
    if (!userData?.public_id) return;
    if (!transcription) {
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description: t("visits.sidebar.messages.noTranscription"),
      });
      return;
    }
    await submitVisitWithoutFile({
      ...(props.title?.trim() ? { title: props.title.trim() } : {}),
      duration: transcription.duration,
      style: formData.style,
      format: formData.format,
      exam: formData.examination,
      template: formData.templateName,
      customTemplatePublicId: formData.customTemplatePublicId ?? undefined,
      language: transcription.language,
      transcription: transcription.transcription,
    });
  };

  return (
    <SheetContent className="w-full sm:max-w-md p-0 sm:rounded-l-2xl">
      <SheetTitle></SheetTitle>
      <SheetDescription></SheetDescription>
      <SheetHeader />
      <div className="flex flex-col flex-1 h-full">
        <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
          <div className="w-full">
            <div className="text-sm text-center mb-4">
              <span className="font-bold">
                {t("components.syncRecordingSheet.id")}
              </span>{" "}
              {props.linkedRecordingSession.id}
            </div>
            {isProviderReady && !finished && <TranscriptionStreamingList />}
            {connectionError && (
              <p role="alert" className="text-sm text-destructive">
                {connectionError}
              </p>
            )}
            {!isProviderReady && !finished && !connectionError && (
              <div className="flex items-center gap-2 justify-center">
                {t("components.syncRecordingSheet.waitingProvider")}{" "}
                <Loader className="animate-spin" />
              </div>
            )}
            {finished && (
              <RecorderForm
                ref={recorderFormRef}
                onSubmitData={handleFormSubmit}
              />
            )}
          </div>
        </div>
        <VisitSubmissionNotice
          disabled={loading}
          error={error}
          recovery={recovery}
          onAbandon={abandon}
        />
        <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
          {finished && (
            <SubscriptionOnlyButton
              className="w-full"
              disabled={loading}
              size={"xl"}
              validations={
                canRetry ? [] : [ValidationSuscriptionButton.RECORDING_USAGES]
              }
              onClick={async () => {
                await recorderFormRef.current?.submitForm();
              }}
            >
              {t("visits.sidebar.submit")}
            </SubscriptionOnlyButton>
          )}
          <ConfirmDialog
            description={t("visits.sidebar.cancelConfirmation.description")}
            open={showCancelConfirmation}
            title={t("visits.sidebar.cancelConfirmation.title")}
            trigger={
              <Button
                className="w-full"
                disabled={loading}
                size={"xl"}
                variant={"grey"}
              >
                {t("visits.sidebar.cancel")}
              </Button>
            }
            onOpenChange={setShowCancelConfirmation}
            onConfirm={() => {
              props.onOpenChange(false);
            }}
          />
        </SheetFooter>
      </div>
    </SheetContent>
  );
};
